import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bank, Plus, PencilSimple, Trash, QrCode, UploadSimple } from "@phosphor-icons/react";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";
const empty = { type: "bank", nama_bank: "", no_rekening: "", atas_nama: "", merchant_name: "", aktif: true };

export default function PaymentMethods() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/payment-methods").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (m) => { setForm({ ...empty, ...m }); setEditing(m.id); };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/payment-methods", form);
      else await api.put(`/payment-methods/${editing}`, form);
      toast.success("Metode pembayaran disimpan"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/payment-methods/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(apiError(e)); } };

  const uploadQris = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("token");
      await api.post(`/payment-methods/${id}/qris`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` } });
      toast.success("Gambar QRIS diunggah"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="animate-fade-up space-y-5" data-testid="payment-methods-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Bank size={26} weight="duotone" className="text-[#7C4DE0]" /><h1 className="font-heading text-3xl font-extrabold text-slate-800">Metode Pembayaran</h1></div>
        <button data-testid="add-method-button" onClick={openNew} className="flex items-center gap-2 rounded-xl bg-[#7C4DE0] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#6A38C7]"><Plus size={18} weight="bold" /> Tambah Metode</button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((m) => (
          <div key={m.id} data-testid={`method-${m.id}`} className="rounded-2xl border border-rose-100/70 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {m.type === "qris" ? <QrCode size={20} className="text-[#6A38C7]" /> : <Bank size={20} className="text-[#6A38C7]" />}
                <span className="font-heading font-bold text-slate-800">{m.type === "qris" ? "QRIS" : m.nama_bank}</span>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${m.aktif ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{m.aktif ? "Aktif" : "Nonaktif"}</span>
            </div>
            {m.type === "bank" ? (
              <p className="mt-2 text-sm text-slate-600">{m.no_rekening} · a.n. {m.atas_nama}</p>
            ) : (
              <div className="mt-2 text-sm text-slate-600">
                <p>{m.merchant_name}</p>
                {m.qris_file_id ? <img alt="QRIS" src={`${API}/public/payment-methods/${m.id}/qris`} className="mt-2 h-28 w-28 rounded-lg border object-contain" /> : <p className="text-xs text-amber-600">Belum ada gambar QRIS</p>}
                <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-600">
                  <UploadSimple size={14} /> Upload QRIS
                  <input data-testid={`qris-upload-${m.id}`} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => e.target.files[0] && uploadQris(m.id, e.target.files[0])} />
                </label>
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <button data-testid={`edit-method-${m.id}`} onClick={() => openEdit(m)} className="flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600"><PencilSimple size={14} /> Edit</button>
              <button data-testid={`delete-method-${m.id}`} onClick={() => del(m.id)} className="flex items-center gap-1 rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600"><Trash size={14} /> Hapus</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-400">Belum ada metode pembayaran.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="method-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "Tambah Metode" : "Edit Metode"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Tipe</label>
              <select data-testid="method-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                <option value="bank">Transfer Bank</option>
                <option value="qris">QRIS</option>
              </select>
            </div>
            {form.type === "bank" ? (
              <>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Nama Bank</label><input data-testid="method-bank" value={form.nama_bank} onChange={(e) => setForm({ ...form, nama_bank: e.target.value })} className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Nomor Rekening</label><input data-testid="method-rekening" value={form.no_rekening} onChange={(e) => setForm({ ...form, no_rekening: e.target.value })} className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Atas Nama</label><input data-testid="method-atasnama" value={form.atas_nama} onChange={(e) => setForm({ ...form, atas_nama: e.target.value })} className={inputCls} /></div>
              </>
            ) : (
              <div><label className="mb-1 block text-xs font-semibold text-slate-500">Nama Merchant</label><input data-testid="method-merchant" value={form.merchant_name} onChange={(e) => setForm({ ...form, merchant_name: e.target.value })} className={inputCls} /><p className="mt-1 text-xs text-slate-400">Gambar QRIS diunggah setelah metode disimpan.</p></div>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="method-aktif" type="checkbox" checked={!!form.aktif} onChange={(e) => setForm({ ...form, aktif: e.target.checked })} /> Aktif (tampil ke pasien)</label>
          </div>
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
            <button data-testid="save-method" onClick={save} className="rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white hover:bg-[#6A38C7]">Simpan</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
